import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import SensorPin from './SensorPin';
import AddSensorForm from './AddSensorForm';
import { PLACEHOLDER_YARD_IMAGE } from '../../mocks/lawnMocks';

/**
 * The yard photo with sensor pins on top.
 *
 * Clicking empty space opens the add-sensor form at that point. Positions are
 * stored as percentages of the rendered image, so they survive any image size
 * or aspect ratio.
 */
export default function PropertyView({
  property,
  sensors,
  selectedSensorId,
  onSelectSensor,
  onAddSensor,
  onChangeImage,
  isAddingSensor,
  isPlacementEnabled,
}) {
  const [pendingPin, setPendingPin] = useState(null);
  const imageWrapperRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleImageClick = (event) => {
    if (!isPlacementEnabled) return;

    const bounds = imageWrapperRef.current?.getBoundingClientRect();
    if (!bounds || bounds.width === 0 || bounds.height === 0) return;

    setPendingPin({
      xPct: ((event.clientX - bounds.left) / bounds.width) * 100,
      yPct: ((event.clientY - bounds.top) / bounds.height) * 100,
    });
  };

  const handleSubmit = async (draft) => {
    await onAddSensor(draft);
    setPendingPin(null);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Client-side only: the image becomes a data URL, never uploaded anywhere.
    const reader = new FileReader();
    reader.onload = () => onChangeImage(String(reader.result));
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const imageUrl = property?.imageUrl || PLACEHOLDER_YARD_IMAGE;
  const usingPlaceholder = !property?.imageUrl;

  return (
    <div className="lawn-property-view">
      <div className="lawn-property-toolbar">
        <div className="lawn-property-hint">
          {isPlacementEnabled
            ? 'Click the yard to place a sensor.'
            : 'Placement is paused while a sensor is open.'}
        </div>
        <div className="lawn-property-toolbar-actions">
          <button
            type="button"
            className="lawn-button-secondary"
            onClick={() => fileInputRef.current?.click()}
          >
            <i className="ri-image-add-line" aria-hidden="true"></i> Use my photo
          </button>
          {!usingPlaceholder && (
            <button
              type="button"
              className="lawn-button-secondary"
              onClick={() => onChangeImage('')}
              title="Return to the placeholder yard"
            >
              Reset photo
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            hidden
          />
        </div>
      </div>

      <div
        className={`lawn-property-canvas${isPlacementEnabled ? ' lawn-property-canvas-armed' : ''}`}
        ref={imageWrapperRef}
        onClick={handleImageClick}
        role="presentation"
      >
        <img src={imageUrl} alt={property?.name ? `${property.name} yard` : 'Property yard'} />

        {sensors.map((sensor) => (
          <SensorPin
            key={sensor.id}
            sensor={sensor}
            onSelect={onSelectSensor}
            isSelected={sensor.id === selectedSensorId}
          />
        ))}

        {pendingPin && (
          <>
            <span
              className="lawn-pin lawn-pin-pending"
              style={{ left: `${pendingPin.xPct}%`, top: `${pendingPin.yPct}%` }}
              aria-hidden="true"
            >
              <span className="lawn-pin-dot" />
            </span>
            <AddSensorForm
              position={pendingPin}
              onSubmit={handleSubmit}
              onCancel={() => setPendingPin(null)}
              isSaving={isAddingSensor}
            />
          </>
        )}

        {sensors.length === 0 && !pendingPin && (
          <p className="lawn-property-empty">
            No sensors yet. Click anywhere on the yard to place your first one.
          </p>
        )}
      </div>

      <ul className="lawn-legend" aria-label="Pin colour key">
        <li>
          <span className="lawn-legend-swatch lawn-legend-online" aria-hidden="true" /> Online
        </li>
        <li>
          <span className="lawn-legend-swatch lawn-legend-low_battery" aria-hidden="true" /> Low
          battery
        </li>
        <li>
          <span className="lawn-legend-swatch lawn-legend-offline" aria-hidden="true" /> Offline
        </li>
      </ul>
    </div>
  );
}

PropertyView.propTypes = {
  property: PropTypes.shape({
    name: PropTypes.string,
    imageUrl: PropTypes.string,
  }),
  sensors: PropTypes.array.isRequired,
  selectedSensorId: PropTypes.string,
  onSelectSensor: PropTypes.func.isRequired,
  onAddSensor: PropTypes.func.isRequired,
  onChangeImage: PropTypes.func.isRequired,
  isAddingSensor: PropTypes.bool,
  isPlacementEnabled: PropTypes.bool,
};

PropertyView.defaultProps = {
  property: null,
  selectedSensorId: null,
  isAddingSensor: false,
  isPlacementEnabled: true,
};
